import { mfaMethodLabels } from "@purlink/shared/auth";
import { roleLabels, roles } from "@purlink/shared/permissions";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { enAU } from "date-fns/locale";
import {
  KeyRound,
  Mail,
  MonitorSmartphone,
  MoreHorizontal,
  Pencil,
  ShieldOff,
  Trash2,
  UserCheck,
  UserCog,
  UserPlus,
  UserX,
  Users,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { PageHeader } from "~/components/shell/app-shell";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import { Avatar, EmptyState, Skeleton } from "~/components/ui/feedback";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toast } from "~/components/ui/toaster";
import { userActions, usersQuery, type AdminUser } from "~/features/users/api";
import { InviteUserDialog } from "~/features/users/invite-dialog";
import { SessionsSheet } from "~/features/users/sessions-sheet";
import { errorMessage, meQuery } from "~/lib/auth";
import { formatDate } from "~/lib/format";

export const Route = createFileRoute("/_app/users")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me?.user.role !== "super_admin") throw redirect({ to: "/dashboard" });
  },
  component: UsersPage,
});

type Action =
  | "rename"
  | "role"
  | "password-reset"
  | "mfa-reset"
  | "deactivate"
  | "reactivate"
  | "resend-invite"
  | "delete";

const statusBadge = {
  active: <Badge tone="success">Active</Badge>,
  invited: <Badge tone="info">Invited</Badge>,
  deactivated: <Badge tone="neutral">Deactivated</Badge>,
} as const;

function lastSignIn(user: AdminUser) {
  if (!user.lastLoginAt) return "Never";
  return formatDistanceToNow(new Date(user.lastLoginAt), { addSuffix: true, locale: enAU });
}

/** Accounts made by the seed script were never invited. */
function invitedOn(user: AdminUser) {
  return user.invitedAt ? formatDate(user.invitedAt) : "—";
}

function UsersPage() {
  const queryClient = useQueryClient();
  const users = useQuery(usersQuery);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pending, setPending] = useState<{ action: Action; user: AdminUser } | null>(null);
  const [sessionsFor, setSessionsFor] = useState<AdminUser | null>(null);
  const [nextRole, setNextRole] = useState<(typeof roles)[number]>("admin");
  const [nextName, setNextName] = useState("");

  const act = useMutation({
    mutationFn: async ({ action, user }: { action: Action; user: AdminUser }) => {
      switch (action) {
        case "rename":
          return userActions.rename(user.id, nextName.trim());
        case "role":
          return userActions.setRole(user.id, nextRole);
        case "password-reset":
          return userActions.passwordReset(user.id);
        case "mfa-reset":
          return userActions.mfaReset(user.id);
        case "deactivate":
          return userActions.deactivate(user.id);
        case "reactivate":
          return userActions.reactivate(user.id);
        case "resend-invite":
          return userActions.resendInvite(user.id);
        case "delete":
          return userActions.remove(user.id);
      }
    },
    onSuccess: async (_data, { action, user }) => {
      await queryClient.invalidateQueries({ queryKey: usersQuery.queryKey });
      const messages: Record<Action, string> = {
        rename: `${user.name} is now ${nextName.trim()}.`,
        role: `${user.name} is now ${nextRole === "admin" ? "an Admin" : "a Super Admin"}.`,
        "password-reset": `Password reset link sent to ${user.email}.`,
        "mfa-reset": `${user.name} will set up two-step sign-in again next time.`,
        deactivate: `${user.name} has been deactivated.`,
        reactivate: `${user.name} can sign in again.`,
        "resend-invite": `Invitation re-sent to ${user.email}.`,
        delete: `${user.name} has been removed.`,
      };
      toast.success(messages[action]);
      setPending(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const open = (action: Action, user: AdminUser) => {
    if (action === "rename") setNextName(user.name);
    if (action === "role") setNextRole(user.role === "admin" ? "super_admin" : "admin");
    setPending({ action, user });
  };

  return (
    <>
      <PageHeader
        title="Users"
        description="Invite team members and manage their access."
        actions={
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus /> Invite user
          </Button>
        }
      />

      <Card padding="none" className="overflow-hidden">
        {users.isPending ? (
          <div className="flex flex-col gap-3 p-5">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : users.isError ? (
          <EmptyState
            icon={<Users />}
            title="Couldn't load users"
            description={errorMessage(users.error)}
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Person</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Two-step sign-in</TableHead>
                    <TableHead>Invited</TableHead>
                    <TableHead>Last sign-in</TableHead>
                    <TableHead className="w-12">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.data.users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <PersonCell user={user} />
                      </TableCell>
                      <TableCell>{roleLabels[user.role]}</TableCell>
                      <TableCell>{statusBadge[user.status]}</TableCell>
                      <TableCell className="text-fg-secondary">
                        {user.mfaMethod ? mfaMethodLabels[user.mfaMethod] : "Not set up"}
                      </TableCell>
                      <TableCell className="text-fg-secondary">{invitedOn(user)}</TableCell>
                      <TableCell className="text-fg-secondary">{lastSignIn(user)}</TableCell>
                      <TableCell>
                        <UserActions user={user} onAction={open} onSessions={setSessionsFor} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Phone list */}
            <ul className="flex flex-col divide-y divide-border md:hidden">
              {users.data.users.map((user) => (
                <li key={user.id} className="flex items-start gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <PersonCell user={user} />
                    <div className="mt-2 flex flex-wrap items-center gap-2 pl-12 text-small text-fg-muted">
                      {statusBadge[user.status]}
                      <span>{roleLabels[user.role]}</span>
                      <span aria-hidden>·</span>
                      {user.invitedAt && (
                        <>
                          <span aria-hidden>·</span>
                          <span>Invited {invitedOn(user)}</span>
                        </>
                      )}
                      <span aria-hidden>·</span>
                      <span>Last sign-in {lastSignIn(user).toLowerCase()}</span>
                    </div>
                  </div>
                  <UserActions user={user} onAction={open} onSessions={setSessionsFor} />
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <SessionsSheet user={sessionsFor} onClose={() => setSessionsFor(null)} />

      {pending && (
        <ConfirmDialog
          open
          onOpenChange={(next) => !next && setPending(null)}
          loading={act.isPending}
          onConfirm={() => act.mutate(pending)}
          {...confirmCopy(pending.action, pending.user)}
        >
          {pending.action === "rename" && (
            <Field label="Name" required>
              <Input
                value={nextName}
                onChange={(event) => setNextName(event.target.value)}
                maxLength={80}
                autoComplete="off"
                autoFocus
              />
            </Field>
          )}
          {pending.action === "role" && (
            <Field label="New role">
              <Select
                value={nextRole}
                onValueChange={(value) => setNextRole(value as typeof nextRole)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role} value={role} disabled={role === pending.user.role}>
                      {roleLabels[role]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </ConfirmDialog>
      )}
    </>
  );
}

function confirmCopy(
  action: Action,
  user: AdminUser,
): {
  title: string;
  description: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  confirmationText?: string;
} {
  const first = user.name.split(" ")[0];
  switch (action) {
    case "rename":
      return {
        title: `Rename ${user.name}`,
        description: "Notes and timeline entries they've already made keep their old name.",
        confirmLabel: "Save name",
      };
    case "role":
      return {
        title: `Change ${user.name}'s role`,
        description: "They'll be signed out so their new permissions take effect.",
        confirmLabel: "Change role",
      };
    case "password-reset":
      return {
        title: `Send ${first} a password reset link?`,
        description: `We'll email ${user.email} a link to choose a new password and sign them out everywhere. You won't see their password.`,
        confirmLabel: "Send reset link",
      };
    case "mfa-reset":
      return {
        title: `Reset ${first}'s two-step sign-in?`,
        description:
          "Use this if they've lost their phone or can't get codes. They'll be signed out and asked to set it up again next time.",
        confirmLabel: "Reset two-step sign-in",
        destructive: true,
      };
    case "deactivate":
      return {
        title: `Deactivate ${user.name}?`,
        description:
          "They'll be signed out and won't be able to sign in until reactivated. Their work stays in Purlink.",
        confirmLabel: "Deactivate",
        destructive: true,
      };
    case "reactivate":
      return {
        title: `Reactivate ${user.name}?`,
        description: "They'll be able to sign in again with their existing password.",
        confirmLabel: "Reactivate",
      };
    case "resend-invite":
      return {
        title: `Resend ${first}'s invitation?`,
        description: `A new link will be emailed to ${user.email}. Earlier links stop working.`,
        confirmLabel: "Resend invitation",
      };
    case "delete":
      return {
        title: `Remove ${user.name}?`,
        description:
          "Their account is permanently deleted. Notes and emails they sent stay on leads under their name. This can't be undone.",
        confirmLabel: "Remove user",
        destructive: true,
        confirmationText: user.email,
      };
  }
}

function PersonCell({ user }: { user: AdminUser }) {
  return (
    <span className="flex min-w-0 items-center gap-3">
      <Avatar name={user.name} />
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-medium text-fg">
          {user.name}
          {user.isSelf && (
            <span className="ml-1.5 text-caption font-normal text-fg-muted">(you)</span>
          )}
        </span>
        <span className="truncate text-small text-fg-muted">{user.email}</span>
      </span>
    </span>
  );
}

function UserActions({
  user,
  onAction,
  onSessions,
}: {
  user: AdminUser;
  onAction: (action: Action, user: AdminUser) => void;
  onSessions: (user: AdminUser) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${user.name}`}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onSelect={() => onSessions(user)}>
          <MonitorSmartphone /> View devices
        </DropdownMenuItem>
        {user.status === "invited" && (
          <DropdownMenuItem onSelect={() => onAction("resend-invite", user)}>
            <Mail /> Resend invitation
          </DropdownMenuItem>
        )}
        {!user.isSelf && (
          <>
            <DropdownMenuItem onSelect={() => onAction("rename", user)}>
              <Pencil /> Rename
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onAction("role", user)}>
              <UserCog /> Change role
            </DropdownMenuItem>
            {user.status === "active" && (
              <DropdownMenuItem onSelect={() => onAction("password-reset", user)}>
                <KeyRound /> Send password reset
              </DropdownMenuItem>
            )}
            {user.mfaMethod && (
              <DropdownMenuItem onSelect={() => onAction("mfa-reset", user)}>
                <ShieldOff /> Reset two-step sign-in
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {user.status === "deactivated" ? (
              <DropdownMenuItem onSelect={() => onAction("reactivate", user)}>
                <UserCheck /> Reactivate
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => onAction("deactivate", user)}>
                <UserX /> Deactivate
              </DropdownMenuItem>
            )}
            <DropdownMenuItem destructive onSelect={() => onAction("delete", user)}>
              <Trash2 /> Remove user
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
